import React, { useEffect, useMemo, useRef, useState } from 'react';

import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import loadingImg from '@assets/img/arts/loading.svg';
import compassIcon from '@assets/img/icons/compass.svg';
import d2d from '@assets/img/icons/d2d-white.png';
import agencyIcon from '@assets/img/icons/Iconly/Light-outline/agency.svg';
import bookingIcon from '@assets/img/icons/Iconly/Light-outline/bookingIcon.svg';
import categoryIcon from '@assets/img/icons/Iconly/Light-outline/Category.svg';
import InvoiceIcon from '@assets/img/icons/Iconly/Light-outline/InvoiceIcon.svg';
import userIcon from '@assets/img/icons/Iconly/Light-outline/User.svg';
import workIcon from '@assets/img/icons/Iconly/Light-outline/Work.svg';
import managementSystem from '@assets/img/icons/management-system.svg';
import { Button } from '@components/Button';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { LoadingErrorsFallback } from '@components/LoadingErrorsFallback';
import { useLogout } from '@hooks/mutation/useLogout';
import { useMaintenanceRedirect } from '@hooks/next/useMaintenanceRedirect';
import { useBookingInquirySearchList } from '@hooks/queries/dashboard/useBookingInquirySearchList';
import { useCompanyItemList } from '@hooks/queries/dashboard/useCompanyItemList';
import { useUser } from '@hooks/queries/useUser';
import { useOpenInVscode } from '@hooks/useOpenInVscode';
import Avatar from '@material-ui/core/Avatar';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuIcon from '@material-ui/icons/Menu';
import RoomIcon from '@material-ui/icons/Room';
import { User, UserHelper } from '@models/User';
import { AdminLayoutManager } from '@store/AdminLayoutManager';
import { AuthManager } from '@store/AuthManager';
import { useCommonStyles, makeMuiStyles } from '@styles';
import { createStrings } from '@utils/local';
import { getBreadcrumb, Meta, MetaHelper, url } from '@utils/pages';
import clsx from 'clsx';
import Color from 'color';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { StatusNotHandled } from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '管理システム',
    editProfile: 'プロフィール編集',
    logout: 'ログアウト',
    members: '会員管理',
    bookingInquiry: 'ブッキング管理',
    toClientSystem: 'お客様システムへ',
    networkErrorPrompt: () => (
      <>
        管理ページの読み込み中にエラーが発生しました。
        <br />
        <br />
        ネットワークを確認し、ページを更新してみてください。
        <br />
        <br />
        問題が解決しない場合は、 開発者にお問い合わせください。
      </>
    ),
    notFoundErrorPrompt: () => (
      <>
        大変申し訳ございません。
        <br />
        お探しのページが見つかりませんでした。
      </>
    ),
  },
});

const drawerWidth = 240;
const mainContentWidth = `calc(100% - ${drawerWidth}px)`;

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    width: '100%',
    height: '100vh',
  },
  appBar: {
    ...common.flexInline,
    alignItems: 'center',
    height: 50,
    background: theme.palette.common.black,
    padding: '1rem 2rem 1rem 3.5rem',
  },
  logo: {
    height: 20,
    width: 100,
    display: 'block',
  },
  appBarTitle: {
    paddingLeft: '0.5rem',
    height: 20,
    width: 136,
  },
  userInformationContainer: {
    ...common.flexInline,
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '7rem',
  },
  menuItemRoot: {
    paddingTop: '1rem',
    paddingBottom: '1rem',
    color: theme.palette.text.secondary,
    '& + *': {
      borderTopStyle: 'solid',
      borderTopWidth: 1,
      borderTopColor: theme.palette.grey['100'],
    },
  },
  drawerToolbar: {
    ...common.flexInline,
    alignItems: 'center',
    height: '3rem',
    flexShrink: 0,
    '$folded &': {
      justifyContent: 'center',
    },
  },
  drawerToolbarButton: {
    ...common.baseButton,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
  },
  drawer: {
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    position: 'static !important' as any,
    backgroundColor: theme.palette.common.black,
    color: theme.palette.background.default,
    '& .MuiDivider-root': {
      backgroundColor: theme.palette.background.default,
      opacity: 0.2,
    },
    '$folded &': {
      width: 50,
    },
  },
  main: {
    ...common.flexInline,
    backgroundColor: '#F0F2F5',
    flex: 1,
    minHeight: 0,
  },
  mainContent: {
    ...common.flexColumn,
    flex: 1,
    width: mainContentWidth,
  },
  mainScrollable: {
    ...common.flexColumn,
    flex: 1,
    overflow: 'auto',
    margin: '20px 30px',
    backgroundColor: theme.palette.background.paper,
  },
  mainBreadcrumbs: {
    ...common.flexInline,
    ...common.horizontalSpace1,
    color: theme.palette.text.secondary,
    alignItems: 'center',
    height: '3rem',
    boxSizing: 'content-box',
    padding: '0 2rem',
    background: theme.palette.background.default,
    borderBottomWidth: 1,
    borderBlockColor: theme.palette.grey['300'],
    borderBottomStyle: 'solid',
  },
  listRoot: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  nested: {
    color: theme.palette.grey['300'],
    height: 40,
    paddingRight: 0,
    '&$selected': {
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  commonIcon: {
    color: theme.palette.background.default,
    fontSize: 20,
    marginLeft: 15,
  },
  itemRoot: {
    height: 40,
    '&:hover $itemHighlight': {
      display: 'initial',
    },
  },
  itemHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 4,
    background: theme.palette.primary.main,
    pointerEvents: 'none',
    display: 'none',
    '&$selected': {
      display: 'initial',
    },
  },
  nestedItemHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    display: 'none',
  },
  subrouteListRoot: {
    background: theme.palette.grey['900'],
  },
  progressRoot: {
    position: 'fixed',
    zIndex: 9999999,
    width: '100%',
    background: Color(theme.palette.primary.main).alpha(0.5).string(),
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  progressRootLoading: {
    opacity: 1,
  },
  selected: {},
  selectedBreadcrumb: {
    fontWeight: 500,
    color: theme.palette.grey['800'],
  },
  loading: {
    ...common.flexCenter,
    flex: 1,
  },
  username: {
    ...common.middleText,
    color: theme.palette.secondary.contrastText,
  },
  badge: {
    ...common.flexCenter,
    ...common.smallerText,
    height: 18,
    minWidth: 18,
    padding: '0 2px',
    borderRadius: '9px',
    marginLeft: 5,
  },
  backgroundWarning: {
    backgroundColor: theme.palette.warning.dark,
  },
  backgroundPrimary: {
    backgroundColor: theme.palette.secondary.main,
  },
  folded: {},
}));

export interface RouteIconProps {
  routeName: string;
}

export const RouteIcon: React.FC<RouteIconProps> = (props) => {
  const classes = {
    ...useStyles(),
  };

  const { routeName } = props;

  switch (routeName) {
    case '/admin/dashboard':
      return (
        <img
          className={classes.commonIcon}
          src={compassIcon}
          alt="Widget"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    case '/admin/members':
      return (
        <img
          className={classes.commonIcon}
          src={userIcon}
          alt="Widget"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    case '/admin/staffs':
      return (
        <img
          className={classes.commonIcon}
          src={workIcon}
          alt="Widget"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    case '/admin/widgets':
      return (
        <img
          className={classes.commonIcon}
          src={categoryIcon}
          alt="Widget"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    case '/admin/booking':
      return (
        <img
          className={classes.commonIcon}
          src={bookingIcon}
          alt="Booking"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    case '/admin/invoice':
      return (
        <img
          className={classes.commonIcon}
          src={InvoiceIcon}
          alt="Invoice"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    case '/admin/agency':
      return (
        <img
          className={classes.commonIcon}
          src={agencyIcon}
          alt="Invoice"
          height={18}
          width={18}
          style={{ marginLeft: 16 }}
        />
      );
    default:
      return null;
  }
};

interface RouteBadgeProps {
  routeName: string;
}

export const RouteBadge: React.FC<RouteBadgeProps> = (props) => {
  const classes = {
    ...useStyles(),
  };
  const user = useUser();
  const isJapanStaff = user?.data ? new UserHelper(user?.data).isJapanStaff : false;
  const membershipForms = useCompanyItemList(
    {
      page: 1,
      page_size: 1,
      has_member_salesperson: false,
    },
    {
      enabled: isJapanStaff,
    },
  );
  const completionRequests = useCompanyItemList(
    {
      page: 1,
      page_size: 1,
      status: 'D2D会員開設申請',
    },
    {
      enabled: isJapanStaff,
    },
  );
  const bookingInquiryCount = useBookingInquirySearchList({ page: 1, page_size: 1 });
  const preAlertBeforeDepartureCount = dashboardPreAlertApis.useGet('/dashboard/pre-alert/count', {
    queries: {
      query: {
        before_departure: true,
        type: ['current'],
      },
    },
  });

  const preAlertAfterDepartureCount = dashboardPreAlertApis.useGet('/dashboard/pre-alert/count', {
    queries: {
      query: {
        before_departure: false,
        has_salesperson_japan: false,
        type: ['current'],
      },
    },
  });

  const isFCChina = user?.data ? new UserHelper(user?.data).isFCChina : false;
  const bookingInquiryCountData = bookingInquiryCount.data?.count;
  const preAlertBeforeDepartureCountData = preAlertBeforeDepartureCount.data?.count;
  const preAlertAfterDepartureCountData = preAlertAfterDepartureCount.data?.count;
  const membershipFormsCountData = membershipForms.data?.count;
  const completionRequestsCountData = completionRequests.data?.count;

  const { routeName } = props;

  switch (routeName) {
    case '/admin/booking/BookingInquiries':
      return bookingInquiryCountData ? (
        <div
          className={clsx(
            classes.badge,
            isFCChina ? classes.backgroundWarning : classes.backgroundPrimary,
          )}
        >
          {bookingInquiryCountData}
        </div>
      ) : null;
    case '/admin/booking/CurrentBookingsBeforeDeparture':
      return preAlertBeforeDepartureCountData && !isFCChina ? (
        <div className={clsx(classes.badge, classes.backgroundPrimary)}>
          {preAlertBeforeDepartureCountData}
        </div>
      ) : null;
    case '/admin/booking/CurrentBookingsAfterDeparture':
      return preAlertAfterDepartureCountData && !isFCChina ? (
        <div className={clsx(classes.badge, classes.backgroundWarning)}>
          {preAlertAfterDepartureCountData}
        </div>
      ) : null;
    case '/admin/members/MembershipForms':
      return membershipFormsCountData ? (
        <div className={clsx(classes.badge, classes.backgroundPrimary)}>
          {membershipFormsCountData}
        </div>
      ) : null;
    case '/admin/members/CompletionRequests':
      return completionRequestsCountData ? (
        <div className={clsx(classes.badge, classes.backgroundPrimary)}>
          {completionRequestsCountData}
        </div>
      ) : null;
    default:
      return null;
  }
};

export interface NavigationButtonProps {
  baseUrl: string;
  title: string;
  user: User | null | undefined;
  selected: boolean;
  onSelect: () => void;
}

export const NavigationButton: React.FC<NavigationButtonProps> = observer((props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { baseUrl, title, user, selected, onSelect } = props;

  const currentPath = useLocation().pathname;

  const folded = AdminLayoutManager.shared.navigationFolded;

  let subroutes = MetaHelper.shared.withPrefix(baseUrl).filter((m) => !m.isDirectory && m.listed);

  if (user == null) {
    subroutes = subroutes.filter((m) => !m.superuserOnly);
  } else {
    subroutes = subroutes.filter((m) => MetaHelper.shared.shouldVisit(m, user));
  }

  const handleClick = () => {
    onSelect();

    if (folded) {
      AdminLayoutManager.shared.setNavigationFolded(false);
    }
  };

  return (
    <div>
      <ListItem disableGutters button onClick={handleClick} className={classes.itemRoot}>
        <ListItemIcon>
          <RouteIcon routeName={baseUrl} />
        </ListItemIcon>
        {!folded && (
          <>
            <div className={classes.middleText} style={{ flex: 1 }}>
              {title}
            </div>
            <div>
              <ExpandMoreButton
                expanded={selected}
                classes={{ root: classes.fillWhiteImportant }}
              />
            </div>
            <div style={{ width: 12 }} />
          </>
        )}
        <div className={clsx(classes.itemHighlight, selected && classes.selected)} />
      </ListItem>
      {!folded && (
        <Collapse in={selected} timeout="auto" unmountOnExit>
          <List classes={{ root: classes.subrouteListRoot }} component="div" disablePadding>
            {subroutes.map((m) => (
              <Link key={m.url} to={m.url}>
                <ListItem
                  button
                  classes={{
                    root: clsx(classes.nested, currentPath === m.url && classes.selected),
                  }}
                >
                  <div style={{ width: 40 }} />
                  <div className={classes.middleText}>
                    {m.title}
                    {user?.role === 'FCChina' && m.titleCn ? `(${m.titleCn})` : ''}
                  </div>
                  <RouteBadge routeName={m.url} />
                  <div className={classes.nestedItemHighlight} />
                </ListItem>
              </Link>
            ))}
          </List>
        </Collapse>
      )}
    </div>
  );
});

export interface LayoutProps {
  title: string;
  loading?: boolean;
  errors?: unknown[];
  children: React.ReactNode | (() => React.ReactNode);
  breadcrumb?: (Meta | string)[];
}

export const Layout: React.FC<LayoutProps> = observer((props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const location = useLocation();

  const { title, children, loading, errors, breadcrumb = getBreadcrumb(location.pathname) } = props;

  const [menuAnchor, setMenuAnchor] = useState<any>(null);
  const appBarRef = useRef<any>();
  const history = useHistory();
  const logout = useLogout();

  const user = useUser(undefined, {
    enabled: AuthManager.shared.isLoggedIn,
  });
  useMaintenanceRedirect();

  const finalLoading = loading || user.isLoading || logout.isLoading;

  let pages = MetaHelper.shared.withPrefix('/admin/').filter((m) => m.isDirectory && m.listed);

  const currentMeta = MetaHelper.shared.withPrefix(location.pathname)[0];

  const isAuthorized = user.data != null && MetaHelper.shared.shouldVisit(currentMeta, user.data!);

  if (user.data) {
    pages = pages.filter((m) => MetaHelper.shared.shouldVisit(m, user.data!));
  } else {
    pages = pages.filter((m) => !m.superuserOnly);
  }

  const selectedPages = AdminLayoutManager.shared.selectedPages;

  useEffect(() => {
    const currentUrl = pages.find((p) => location.pathname.startsWith(p.url))?.url;
    if (currentUrl) {
      runInAction(() => {
        AdminLayoutManager.shared.selectedPages.add(currentUrl);
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    // No page is permitted to the user here.
    if (AuthManager.shared.isLoggedIn && !isAuthorized && user.isSuccess) {
      if (currentMeta.url.startsWith(url('/admin/booking/Dashboard'))) {
        history.push(url('/admin/booking/BookingInquiries'));
      } else {
        history.push(url('/NotAuthorized'));
      }
    }
  }, [AuthManager.shared.isLoggedIn, isAuthorized, user.isSuccess]);

  if (import.meta.env.DEV) {
    useOpenInVscode();
  }

  const renderUserInformation = () => {
    return (
      <div className={classes.userInformationContainer}>
        <Avatar alt={new UserHelper(user.data!).fullname} src={user.data!.profile.avatar_sm} />
        <div style={{ width: 10 }} />
        <div className={classes.username} style={{ flex: 1, textAlign: 'center' }}>
          {new UserHelper(user.data!).fullname}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (user.data == null || !MetaHelper.shared.shouldVisit(currentMeta, user.data!)) {
      return null;
    }
    if (children instanceof Function) {
      return children();
    }
    return children;
  };

  const renderLoading = () => {
    return (
      <div className={classes.loading}>
        <img src={loadingImg} alt="Loading..." />
      </div>
    );
  };

  const renderMenu = () => {
    return (
      <Menu
        anchorEl={menuAnchor}
        keepMounted
        open={Boolean(menuAnchor)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        getContentAnchorEl={undefined}
        elevation={4}
      >
        <MenuItem
          classes={{ root: classes.menuItemRoot }}
          onClick={() => {
            history.push('/admin/staffs/Profile');
            handleClose();
          }}
        >
          {strings.editProfile}
        </MenuItem>
        <MenuItem
          classes={{ root: classes.menuItemRoot }}
          onClick={async () => {
            await logout.mutateAsync();
            history.push('/admin/auth/Login');
            handleClose();
          }}
        >
          {strings.logout}
        </MenuItem>
      </Menu>
    );
  };

  const renderDrawerToolbar = () => {
    return (
      <div className={classes.drawerToolbar}>
        <div style={{ flex: 1 }} />
        <div
          className={classes.drawerToolbarButton}
          onClick={() => {
            AdminLayoutManager.shared.setNavigationFolded(
              !AdminLayoutManager.shared.navigationFolded,
            );
          }}
        >
          <MenuIcon />
        </div>
      </div>
    );
  };

  const renderDrawerList = () => {
    return (
      <List component="nav" aria-labelledby="nested-list-subheader" className={classes.listRoot}>
        {pages.map((m) => (
          <NavigationButton
            key={m.url}
            baseUrl={m.url}
            title={m.title!}
            selected={selectedPages.has(m.url)}
            onSelect={() =>
              runInAction(() => {
                if (selectedPages.has(m.url)) {
                  selectedPages.delete(m.url);
                } else {
                  selectedPages.add(m.url);
                }
              })
            }
            user={user.data}
          />
        ))}
      </List>
    );
  };

  const renderDrawer = () => {
    return (
      <Drawer
        className={clsx(
          classes.drawer,
          AdminLayoutManager.shared.navigationFolded && classes.folded,
        )}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor="left"
      >
        {renderDrawerToolbar()}
        <Divider />
        {renderDrawerList()}
      </Drawer>
    );
  };

  const renderBreadcrumbs = () => {
    return (
      <div className={classes.mainBreadcrumbs}>
        <RoomIcon />
        <Breadcrumbs aria-label="breadcrumb">
          {breadcrumb.map((segment, i) =>
            typeof segment === 'string' || segment.isDirectory ? (
              <span
                key={i}
                className={clsx(
                  classes.middleText,
                  i === breadcrumb.length - 1 && classes.selectedBreadcrumb,
                )}
              >
                {typeof segment === 'string' ? segment : segment.title}
              </span>
            ) : (
              <Link
                key={i}
                className={clsx(
                  classes.middleText,
                  i === breadcrumb.length - 1 && classes.selectedBreadcrumb,
                )}
                to={segment.url}
              >
                {segment.title}
              </Link>
            ),
          )}
        </Breadcrumbs>
      </div>
    );
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const errorPrompt = useMemo(() => {
    if (errors?.some((e) => e instanceof StatusNotHandled && e.status === 404)) {
      return strings.notFoundErrorPrompt();
    } else {
      return strings.networkErrorPrompt();
    }
  }, [errors]);

  return (
    <div className={classes.container}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${title} - D2D`}</title>
      </Helmet>

      <div ref={appBarRef} className={classes.appBar}>
        <a href="/admin/members/MembershipForms">
          <img className={clsx(classes.logo, classes.baseButton)} src={d2d} alt="D2D Logo" />
        </a>

        <img className={classes.appBarTitle} alt="Management System" src={managementSystem} />

        <Link to={url('/utilities/Dashboard')}>
          <Button
            color="primary"
            size="small"
            shape="round"
            text={strings.toClientSystem}
            style={{ marginLeft: '2rem' }}
          />
        </Link>

        {import.meta.env.DEV && (
          <Link to={url('/dev/Accounts')}>
            <Button
              color="secondary"
              size="small"
              shape="round"
              text="DEV"
              style={{ marginLeft: '2rem' }}
            />
          </Link>
        )}
        <div style={{ flex: 1 }} />
        {user.data && renderUserInformation()}
        <ExpandMoreButton
          expanded={Boolean(menuAnchor)}
          color="action"
          onClick={() => setMenuAnchor(menuAnchor ? null : appBarRef.current)}
          classes={{
            root: classes.fillWhiteImportant,
          }}
        />
        {renderMenu()}
      </div>
      <div className={classes.main}>
        {renderDrawer()}
        <div className={classes.mainContent}>
          {renderBreadcrumbs()}
          <div className={classes.mainScrollable}>
            {finalLoading ? (
              renderLoading()
            ) : (
              <LoadingErrorsFallback prompt={errorPrompt} errors={errors}>
                {renderContent}
              </LoadingErrorsFallback>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
